import React, { useState, useEffect } from 'react';
import { notesAPI, casesAPI, clientsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import { Plus, Edit2, Trash2, Lock, FileText } from 'lucide-react';
import { toast } from 'react-toastify';

const EMPTY = { case: '', client: '', subject: '', content: '', is_private: false };

export default function Notes() {
  const [notes, setNotes] = useState([]);
  const [cases, setCases] = useState([]);
  const [clients, setClients] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();

  const load = async () => {
    const { data } = await notesAPI.list();
    setNotes(data.results || data);
  };

  useEffect(() => {
    load();
    casesAPI.list().then(r => setCases(r.data.results || r.data));
    clientsAPI.list().then(r => setClients(r.data.results || r.data));
  }, []);

  const openAdd = () => { setEditing(null); setForm(EMPTY); setModalOpen(true); };
  const openEdit = (n) => { setEditing(n); setForm({ ...n }); setModalOpen(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) { await notesAPI.update(editing.id, form); toast.success('Note updated'); }
      else { await notesAPI.create(form); toast.success('Note added'); }
      setModalOpen(false);
      load();
    } catch { toast.error('Failed to save note'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this note?')) return;
    await notesAPI.delete(id);
    toast.success('Note deleted');
    load();
  };

  return (
    <div className="p-6">
      <PageHeader
        title="Notes"
        subtitle={`${notes.length} notes`}
        actions={<button className="btn-primary" onClick={openAdd}><Plus size={16} />Add Note</button>}
      />

      {/* Notes Table */}
      <div className="card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              {['Subject', 'Note', 'Case / Client', 'Date', 'Author', ''].map(h => (
                <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {notes.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-16 text-gray-400">
                  <FileText size={32} className="mx-auto mb-2 text-gray-300" />
                  No notes yet. Add your first note.
                </td>
              </tr>
            ) : notes.map(n => (
              <tr key={n.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-900 max-w-xs">
                  <div className="flex items-center gap-2">
                    {n.subject || <span className="text-gray-400 italic">No subject</span>}
                    {n.is_private && (
                      <span className="flex items-center gap-0.5 text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                        <Lock size={10} /> Private
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-gray-600 max-w-sm">
                  <p className="truncate">{n.content}</p>
                </td>
                <td className="px-4 py-3 text-gray-500 text-xs">
                  {n.case_title && <span className="text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">{n.case_title}</span>}
                </td>
                <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                  {new Date(n.created_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </td>
                <td className="px-4 py-3 text-gray-600">
                  <div className="flex items-center gap-1.5">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-blue-700 text-xs font-bold">{n.employee_name?.[0]}</span>
                    </div>
                    <span className="text-xs">{n.employee_name}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <button className="p-1.5 text-gray-400 hover:text-amber-600 rounded" onClick={() => openEdit(n)}><Edit2 size={13} /></button>
                    <button className="p-1.5 text-gray-400 hover:text-red-600 rounded" onClick={() => handleDelete(n.id)}><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit Note' : 'New Note'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Subject *</label>
            <input
              className="input"
              required
              placeholder="e.g. Client meeting follow-up"
              value={form.subject || ''}
              onChange={e => setForm({ ...form, subject: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Linked Case</label>
              <select className="input" value={form.case || ''} onChange={e => setForm({ ...form, case: e.target.value })}>
                <option value="">None</option>
                {cases.map(c => <option key={c.id} value={c.id}>{c.case_title}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Linked Client</label>
              <select className="input" value={form.client || ''} onChange={e => setForm({ ...form, client: e.target.value })}>
                <option value="">None</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.client_name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="label">Note *</label>
            <textarea className="input" rows={6} required value={form.content || ''} onChange={e => setForm({ ...form, content: e.target.value })} placeholder="Write your note here..." />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="private" checked={form.is_private === true} onChange={e => setForm({ ...form, is_private: e.target.checked })} className="rounded" />
            <label htmlFor="private" className="text-sm text-gray-700 flex items-center gap-1"><Lock size={13} /> Private (only visible to me)</label>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving...' : editing ? 'Update' : 'Save Note'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
